import { useState, useRef } from "react";
import Cookies from "universal-cookie";
import { toast } from "react-toastify";
import {
  MdLocalShipping,
  MdSchedule,
  MdAssignment,
  MdFlightTakeoff,
  MdDirectionsBoat,
  MdAdd,
  MdRemove,
  MdUploadFile,
  MdCheckCircle,
} from "react-icons/md";
import { FaFileInvoice, FaTruck, FaTrain } from "react-icons/fa";
import "../../stylesheets/CreateShipment.scss";

/**
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onCreated?: (created) => void
 * - accounts?: string[]
 */
const CreateShipments = ({ isOpen, onClose, onCreated, accounts = [] }) => {
  const cookies = new Cookies();
  const token = cookies.get("token_user");
  const USER_URL = process.env.REACT_APP_API_URL + "/user";

  // ── Shipment type toggle ──────────────────────────────────────────
  const [shipmentType, setShipmentType] = useState("air"); // "air" | "ocean" | "trucker" | "rail"

  // ── Shared fields ─────────────────────────────────────────────────
  const [accountNo, setAccountNo] = useState("");
  const [eta, setEta] = useState("");
  const [customerRef, setCustomerRef] = useState("");
  const [note, setNote] = useState("");

  // ── Air fields ────────────────────────────────────────────────────
  const [masterBL, setMasterBL] = useState("");
  const [hawbs, setHawbs] = useState([""]);

  // ── Ocean fields ──────────────────────────────────────────────────
  const [mbls, setMbls] = useState([""]);
  const [hbls, setHbls] = useState([""]);
  const [containers, setContainers] = useState([""]);
  const [clientFillsIsf, setClientFillsIsf] = useState(null);
  const [isfFile, setIsfFile] = useState(null);
  const isfInputRef = useRef(null);

  // ── Trucker fields ────────────────────────────────────────────────
  const [truckerMbls, setTruckerMbls] = useState([""]);
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("lbs");
  const [palletsCartons, setPalletsCartons] = useState("");

  // ── Rail fields ───────────────────────────────────────────────────
  const [railMbls, setRailMbls] = useState([""]);
  const [railContainers, setRailContainers] = useState([""]);

  // ── Document Upload fields ────────────────────────────────────────
  const [arrivalNoticeFiles, setArrivalNoticeFiles] = useState([]);
  const [billOfLadingFiles, setBillOfLadingFiles] = useState([]);
  const [commercialInvoiceFiles, setCommercialInvoiceFiles] = useState([]);
  const [packingSlipFiles, setPackingSlipFiles] = useState([]);
  const [isfFiles, setIsfFiles] = useState([]);

  const arrivalNoticeInputRef = useRef(null);
  const billOfLadingInputRef = useRef(null);
  const commercialInvoiceInputRef = useRef(null);
  const packingSlipInputRef = useRef(null);

  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  // ── Helpers ───────────────────────────────────────────────────────
  const reset = () => {
    setAccountNo("");
    setEta("");
    setCustomerRef("");
    setNote("");
    setMasterBL("");
    setHawbs([""]);
    setMbls([""]);
    setHbls([""]);
    setContainers([""]);
    setClientFillsIsf(null);
    setIsfFile(null);
    setTruckerMbls([""]);
    setWeight("");
    setWeightUnit("lbs");
    setPalletsCartons("");
    setRailMbls([""]);
    setRailContainers([""]);
    setArrivalNoticeFiles([]);
    setBillOfLadingFiles([]);
    setCommercialInvoiceFiles([]);
    setPackingSlipFiles([]);
    setIsfFiles([]);
    if (arrivalNoticeInputRef.current) arrivalNoticeInputRef.current.value = "";
    if (billOfLadingInputRef.current) billOfLadingInputRef.current.value = "";
    if (commercialInvoiceInputRef.current) commercialInvoiceInputRef.current.value = "";
    if (packingSlipInputRef.current) packingSlipInputRef.current.value = "";
    if (isfInputRef.current) isfInputRef.current.value = "";
  };

  const close = () => {
    if (loading) return;
    reset();
    onClose && onClose();
  };

  const handleAddFiles = (e, setFiles) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      setFiles((prev) => [...prev, ...selectedFiles]);
    }
    e.target.value = "";
  };

  const appendDocFiles = (formData, paramName, files) => {
    files.forEach((file) => {
      formData.append(`${paramName}[]`, file);
    });
  };
  const updateArr = (arr, setArr, idx, value) => {
    const copy = [...arr];
    copy[idx] = value;
    setArr(copy);
  };
  const addArr = (arr, setArr) => setArr([...arr, ""]);
  const removeArr = (arr, setArr, idx) =>
    setArr(arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr);

  // ── Validation ────────────────────────────────────────────────────
  const validateAir = () => {
    if (!accountNo.trim()) { toast.error("Account No is required"); return false; }
    if (!masterBL.trim())  { toast.error("Master Air Waybill is required"); return false; }
    return true;
  };

  const validateOcean = () => {
    if (!accountNo.trim()) { toast.error("Account No is required"); return false; }
    if (!mbls.filter(v => v.trim()).length) { toast.error("At least one Master Bill of Lading is required"); return false; }
    if (!containers.filter(v => v.trim()).length) { toast.error("At least one container number is required"); return false; }
    if (clientFillsIsf === null) { toast.error("Please indicate who fills the ISF"); return false; }
    if (!clientFillsIsf && !isfFiles.length) { toast.error("ISF document is required when Atlantic fills the ISF"); return false; }
    return true;
  };

  const validateTrucker = () => {
    if (!accountNo.trim()) { toast.error("Account No is required"); return false; }
    if (!truckerMbls.filter(v => v.trim()).length) { toast.error("PAPS# is required"); return false; }
    return true;
  };

  const validateRail = () => {
    if (!accountNo.trim()) { toast.error("Account No is required"); return false; }
    if (!railMbls.filter(v => v.trim()).length) { toast.error("At least one Master Bill of Lading is required"); return false; }
    if (!railContainers.filter(v => v.trim()).length) { toast.error("At least one container number is required"); return false; }
    return true;
  };

  // ── Submit: Air ───────────────────────────────────────────────────
  const handleCreateAir = async () => {
    if (!validateAir()) return;
    setLoading(true);
    const formData = new FormData();
    formData.append("account_no", accountNo.trim());
    if (eta) formData.append("est_time_arrive", `${eta} 00:00:00`);
    if (customerRef.trim()) formData.append("customer_reference_no", customerRef.trim());
    if (note.trim()) formData.append("note", note.trim());
    formData.append("master_bill_lading", masterBL.trim());
    const hblVal = hawbs.filter(v => v.trim()).join(", ");
    if (hblVal) formData.append("house_bill_lading", hblVal);
    appendDocFiles(formData, "arrival_notice", arrivalNoticeFiles);
    appendDocFiles(formData, "bill_of_lading", billOfLadingFiles);
    appendDocFiles(formData, "commercial_invoice", commercialInvoiceFiles);
    appendDocFiles(formData, "packing_slip", packingSlipFiles);

    try {
      const res = await fetch(`${USER_URL}/shipments/create_shipment`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        const firstErr = result?.errors ? Object.values(result.errors)?.[0]?.[0] : null;
        toast.error(firstErr || result?.message || "Failed to create shipment.");
        return;
      }
      toast.success("Air shipment created successfully");
      onCreated && onCreated(result?.data);
      close();
    } catch (e) {
      toast.error("Unable to create shipment. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // ── Submit: Ocean ─────────────────────────────────────────────────
  const handleCreateOcean = async () => {
    if (!validateOcean()) return;
    setLoading(true);
    const formData = new FormData();
    formData.append("account_no", accountNo.trim());
    if (eta) formData.append("est_time_arrive", `${eta} 00:00:00`);
    if (customerRef.trim()) formData.append("customer_reference_no", customerRef.trim());
    if (note.trim()) formData.append("note", note.trim());
    mbls.filter(v => v.trim()).forEach((v, i) => formData.append(`master_bill_ladings[${i}]`, v.trim()));
    hbls.filter(v => v.trim()).forEach((v, i) => formData.append(`house_bill_ladings[${i}]`, v.trim()));
    containers.filter(v => v.trim()).forEach((v, i) => formData.append(`containers[${i}]`, v.trim()));
    formData.append("client_fills_isf", clientFillsIsf ? "1" : "0");
    if (!clientFillsIsf) appendDocFiles(formData, "isf_document", isfFiles);
    appendDocFiles(formData, "arrival_notice", arrivalNoticeFiles);
    appendDocFiles(formData, "bill_of_lading", billOfLadingFiles);
    appendDocFiles(formData, "commercial_invoice", commercialInvoiceFiles);
    appendDocFiles(formData, "packing_slip", packingSlipFiles);
    try {
      const res = await fetch(`${USER_URL}/shipments/create_ocean_shipment`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        const firstErr = result?.errors ? Object.values(result.errors)?.[0]?.[0] : null;
        toast.error(firstErr || result?.message || "Failed to create ocean shipment.");
        return;
      }
      toast.success("Ocean shipment created successfully");
      onCreated && onCreated(result?.data);
      close();
    } catch (e) {
      toast.error("Unable to create shipment. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // ── Submit: Trucker ───────────────────────────────────────────────
  const handleCreateTrucker = async () => {
    if (!validateTrucker()) return;
    setLoading(true);
    const formData = new FormData();
    formData.append("account_no", accountNo.trim());
    if (eta) formData.append("est_time_arrive", `${eta} 00:00:00`);
    if (customerRef.trim()) formData.append("customer_reference_no", customerRef.trim());
    if (note.trim()) formData.append("note", note.trim());
    truckerMbls.filter(v => v.trim()).forEach((v, i) => formData.append(`master_bill_ladings[${i}]`, v.trim()));
    if (weight.trim()) formData.append("weight", `${weight.trim()} ${weightUnit}`);
    if (palletsCartons.trim()) formData.append("pallets_cartons", palletsCartons.trim());
    appendDocFiles(formData, "arrival_notice", arrivalNoticeFiles);
    appendDocFiles(formData, "bill_of_lading", billOfLadingFiles);
    appendDocFiles(formData, "commercial_invoice", commercialInvoiceFiles);
    appendDocFiles(formData, "packing_slip", packingSlipFiles);
    try {
      const res = await fetch(`${USER_URL}/shipments/create_trucker_shipment`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        const firstErr = result?.errors ? Object.values(result.errors)?.[0]?.[0] : null;
        toast.error(firstErr || result?.message || "Failed to create trucker shipment.");
        return;
      }
      toast.success("Trucker shipment created successfully");
      onCreated && onCreated(result?.data);
      close();
    } catch (e) {
      toast.error("Unable to create shipment. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // ── Submit: Rail ──────────────────────────────────────────────────
  const handleCreateRail = async () => {
    if (!validateRail()) return;
    setLoading(true);
    const formData = new FormData();
    formData.append("account_no", accountNo.trim());
    if (eta) formData.append("est_time_arrive", `${eta} 00:00:00`);
    if (customerRef.trim()) formData.append("customer_reference_no", customerRef.trim());
    if (note.trim()) formData.append("note", note.trim());
    railMbls.filter(v => v.trim()).forEach((v, i) => formData.append(`master_bill_ladings[${i}]`, v.trim()));
    railContainers.filter(v => v.trim()).forEach((v, i) => formData.append(`containers[${i}]`, v.trim()));
    appendDocFiles(formData, "arrival_notice", arrivalNoticeFiles);
    appendDocFiles(formData, "bill_of_lading", billOfLadingFiles);
    appendDocFiles(formData, "commercial_invoice", commercialInvoiceFiles);
    appendDocFiles(formData, "packing_slip", packingSlipFiles);
    try {
      const res = await fetch(`${USER_URL}/shipments/create_rail_shipment`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        const firstErr = result?.errors ? Object.values(result.errors)?.[0]?.[0] : null;
        toast.error(firstErr || result?.message || "Failed to create rail shipment.");
        return;
      }
      toast.success("Rail shipment created successfully");
      onCreated && onCreated(result?.data);
      close();
    } catch (e) {
      toast.error("Unable to create shipment. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () =>
    shipmentType === "air"     ? handleCreateAir()     :
    shipmentType === "ocean"   ? handleCreateOcean()   :
    shipmentType === "trucker" ? handleCreateTrucker() :
    handleCreateRail();

  const typeLabel = shipmentType === "air" ? "Air" : shipmentType === "ocean" ? "Ocean" : shipmentType === "trucker" ? "Trucker" : "Rail";

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="create-shipment-modal__backdrop" onClick={close}>
      <div className="create-shipment-modal__panel" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="create-shipment-modal__header">
          <h3>Create {typeLabel} Shipment</h3>
          <button className="cs__close" onClick={close} aria-label="Close">&times;</button>
        </div>

        {/* Type toggle */}
        <div className="cs__type-toggle">
          <button
            type="button"
            className={`cs__type-btn${shipmentType === "air" ? " active" : ""}`}
            onClick={() => setShipmentType("air")}
          >
            <MdFlightTakeoff /> Air
          </button>
          <button
            type="button"
            className={`cs__type-btn${shipmentType === "ocean" ? " active" : ""}`}
            onClick={() => setShipmentType("ocean")}
          >
            <MdDirectionsBoat /> Ocean
          </button>
          <button
            type="button"
            className={`cs__type-btn${shipmentType === "trucker" ? " active" : ""}`}
            onClick={() => setShipmentType("trucker")}
          >
            <FaTruck /> Trucker
          </button>
          <button
            type="button"
            className={`cs__type-btn${shipmentType === "rail" ? " active" : ""}`}
            onClick={() => setShipmentType("rail")}
          >
            <FaTrain /> Rail
          </button>
        </div>

        <div className="create-shipment-modal__body">
          <div className="individual warehouse">

            {/* ── Shared: Account ── */}
            <div className="input-section name-phone">
              <div className="input-container">
                <label className="input-label">Account No <span className="req">*</span></label>
                <div className="input">
                  <span className="input-icon"><MdLocalShipping /></span>
                  <select value={accountNo} onChange={(e) => setAccountNo(e.target.value)}>
                    <option value="" disabled>Select account</option>
                    {[...accounts].sort((a, b) => String(a || "").localeCompare(String(b || ""))).map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* ── Shared: Ref & ETA ── */}
            <div className="input-section name-phone">
              <div className="input-container">
                <label className="input-label">Customer Reference No</label>
                <div className="input">
                  <span className="input-icon"><MdAssignment /></span>
                  <input
                    type="text"
                    value={customerRef}
                    onChange={(e) => setCustomerRef(e.target.value)}
                    placeholder="PO / Ref (optional)"
                  />
                </div>
              </div>
              <div className="input-container">
                <label className="input-label">Estimated Time of Arrival</label>
                <div className="input">
                  <span className="input-icon"><MdSchedule /></span>
                  <input type="date" value={eta} onChange={(e) => setEta(e.target.value)} />
                </div>
              </div>
            </div>

            {/* ── Shared: Notes ── */}
            <div className="input-section name-phone">
              <div className="input-container" style={{ width: "100%" }}>
                <label className="input-label">Notes regarding shipment</label>
                <div className="input cs__textarea-input">
                  <span className="input-icon"><MdAssignment /></span>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add note regarding shipment (optional)"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* ────────── AIR FIELDS ────────── */}
            {shipmentType === "air" && (
              <div className="input-section">
                <div className="input-container">
                  <label className="input-label">Master Air Waybill (MAWB) <span className="req">*</span></label>
                  <div className="input">
                    <span className="input-icon"><FaFileInvoice /></span>
                    <input
                      type="text"
                      value={masterBL}
                      onChange={(e) => setMasterBL(e.target.value.replace(/-/g, ""))}
                      placeholder="MAWB number"
                    />
                  </div>
                </div>
                <div className="input-container">
                  <label className="input-label">House Air Waybill (HAWB)</label>
                  <div className="cs__multi-section" style={{ marginTop: 0 }}>
                    {hawbs.map((v, i) => (
                      <div className="cs__multi-row" key={i}>
                        <div className="input">
                          <span className="input-icon"><FaFileInvoice /></span>
                          <input
                            type="text"
                            value={v}
                            onChange={(e) => updateArr(hawbs, setHawbs, i, e.target.value.replace(/-/g, ""))}
                            placeholder={`HAWB #${i + 1} (optional)`}
                          />
                        </div>
                        {hawbs.length > 1 && (
                          <button type="button" className="cs__remove-btn" onClick={() => removeArr(hawbs, setHawbs, i)}>
                            <MdRemove />
                          </button>
                        )}
                      </div>
                    ))}
                    <button type="button" className="cs__add-btn" style={{ marginTop: '6px' }} onClick={() => addArr(hawbs, setHawbs)}>
                      <MdAdd /> Add HAWB
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ────────── OCEAN FIELDS ────────── */}
            {shipmentType === "ocean" && (
              <>
                {/* MBL list */}
                <div className="cs__multi-section">
                  <div className="cs__multi-header">
                    <span className="cs__multi-label">Master Bill of Lading (MBL) <span className="req">*</span></span>
                    <button type="button" className="cs__add-btn" onClick={() => addArr(mbls, setMbls)}>
                      <MdAdd /> Add MBL
                    </button>
                  </div>
                  {mbls.map((v, i) => (
                    <div className="cs__multi-row" key={i}>
                      <div className="input">
                        <span className="input-icon"><FaFileInvoice /></span>
                        <input
                          type="text"
                          value={v}
                          onChange={(e) => updateArr(mbls, setMbls, i, e.target.value.replace(/-/g, ""))}
                          placeholder={`MBL# ${i + 1}`}
                        />
                      </div>
                      {mbls.length > 1 && (
                        <button type="button" className="cs__remove-btn" onClick={() => removeArr(mbls, setMbls, i)}>
                          <MdRemove />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* HBL list */}
                <div className="cs__multi-section">
                  <div className="cs__multi-header">
                    <span className="cs__multi-label">House Bill of Lading (HBL)</span>
                    <button type="button" className="cs__add-btn" onClick={() => addArr(hbls, setHbls)}>
                      <MdAdd /> Add HBL
                    </button>
                  </div>
                  {hbls.map((v, i) => (
                    <div className="cs__multi-row" key={i}>
                      <div className="input">
                        <span className="input-icon"><FaFileInvoice /></span>
                        <input
                          type="text"
                          value={v}
                          onChange={(e) => updateArr(hbls, setHbls, i, e.target.value.replace(/-/g, ""))}
                          placeholder={`HBL #${i + 1} (optional)`}
                        />
                      </div>
                      {hbls.length > 1 && (
                        <button type="button" className="cs__remove-btn" onClick={() => removeArr(hbls, setHbls, i)}>
                          <MdRemove />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Container list */}
                <div className="cs__multi-section">
                  <div className="cs__multi-header">
                    <span className="cs__multi-label">Container Numbers <span className="req">*</span></span>
                    <button type="button" className="cs__add-btn" onClick={() => addArr(containers, setContainers)}>
                      <MdAdd /> Add Container
                    </button>
                  </div>
                  {containers.map((v, i) => (
                    <div className="cs__multi-row" key={i}>
                      <div className="input">
                        <span className="input-icon"><MdLocalShipping /></span>
                        <input
                          type="text"
                          value={v}
                          onChange={(e) => updateArr(containers, setContainers, i, e.target.value.replace(/-/g, ""))}
                          placeholder={`Container #${i + 1}`}
                        />
                      </div>
                      {containers.length > 1 && (
                        <button type="button" className="cs__remove-btn" onClick={() => removeArr(containers, setContainers, i)}>
                          <MdRemove />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* ISF section */}
                <div className="cs__isf-section">
                  <p className="cs__isf-question">
                    Who will fill the ISF (Importer Security Filing)?
                    <span className="req"> *</span>
                  </p>
                  <div className="cs__isf-options">
                    <button
                      type="button"
                      className={`cs__isf-opt${clientFillsIsf === true ? " selected" : ""}`}
                      onClick={() => { setClientFillsIsf(true); setIsfFile(null); }}
                    >
                      {clientFillsIsf === true && <MdCheckCircle className="cs__isf-check" />}
                      ISF filed by a different broker
                    </button>
                    <button
                      type="button"
                      className={`cs__isf-opt${clientFillsIsf === false ? " selected" : ""}`}
                      onClick={() => setClientFillsIsf(false)}
                    >
                      {clientFillsIsf === false && <MdCheckCircle className="cs__isf-check" />}
                      AFB to file ISF
                    </button>
                  </div>
                  {clientFillsIsf === false && (
                    <div className="cs__isf-upload">
                      <p className="cs__isf-upload-hint">
                        Please upload the ISF document <span className="req">*</span>
                      </p>
                      <div className="cs__upload-wrapper">
                        <input
                          ref={isfInputRef}
                          type="file"
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.PDF,.JPG,.JPEG,.PNG,.DOC,.DOCX,.XLS,.XLSX"
                          style={{ display: "none" }}
                          onChange={(e) => handleAddFiles(e, setIsfFiles)}
                        />
                        <button
                          type="button"
                          className="cs__upload-btn"
                          onClick={() => isfInputRef.current?.click()}
                        >
                          <MdUploadFile />
                          <span className="cs__upload-text">
                            Upload ISF Document
                          </span>
                        </button>
                      </div>
                      {isfFiles.length > 0 && (
                        <div className="cs__doc-file-list">
                          {isfFiles.map((file, idx) => (
                            <div key={idx} className="cs__doc-file-chip">
                              <span className="cs__file-name">📎 {file.name}</span>
                              <button
                                type="button"
                                className="cs__remove-file-btn"
                                onClick={() => setIsfFiles((prev) => prev.filter((_, i) => i !== idx))}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ────────── TRUCKER FIELDS ────────── */}
            {shipmentType === "trucker" && (
              <>
                <div className="input-section" style={{ marginBottom: 0 }}>
                  <div className="input-container">
                    <label className="input-label">PAPS# <span className="req">*</span></label>
                    <div className="input">
                      <span className="input-icon"><FaFileInvoice /></span>
                      <input
                        type="text"
                        value={truckerMbls[0] || ""}
                        onChange={(e) => updateArr(truckerMbls, setTruckerMbls, 0, e.target.value.replace(/-/g, ""))}
                        placeholder="PAPS#"
                      />
                    </div>
                  </div>
                </div>

                <div className="input-section name-phone" style={{ marginTop: "12px" }}>
                  <div className="input-container">
                    <label className="input-label">Shipment Weight</label>
                    <div className="input" style={{ position: "relative" }}>
                      <span className="input-icon"><MdLocalShipping /></span>
                      <input
                        type="text"
                        value={weight}
                        onChange={(e) => setWeight(e.target.value.replace(/[^0-9.]/g, ""))}
                        placeholder="e.g. 5000 (optional)"
                        style={{ paddingRight: "72px" }}
                      />
                      <div style={{
                        position: "absolute", right: "6px", top: "50%",
                        transform: "translateY(-50%)",
                        display: "flex", gap: "2px",
                      }}>
                        {["lbs", "kg"].map(unit => (
                          <button
                            key={unit}
                            type="button"
                            onClick={() => setWeightUnit(unit)}
                            style={{
                              padding: "2px 8px",
                              fontSize: "11px",
                              fontWeight: 600,
                              borderRadius: "4px",
                              border: "1px solid #ccc",
                              cursor: "pointer",
                              background: weightUnit === unit ? "#1a6e3c" : "#f5f5f5",
                              color: weightUnit === unit ? "#fff" : "#555",
                              transition: "background 0.15s, color 0.15s",
                            }}
                          >
                            {unit}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="input-container">
                    <label className="input-label">Pallets / Cartons Amount</label>
                    <div className="input">
                      <span className="input-icon"><MdAssignment /></span>
                      <input
                        type="text"
                        value={palletsCartons}
                        onChange={(e) => setPalletsCartons(e.target.value)}
                        placeholder="e.g. 10 pallets / 20 cartons (optional)"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ────────── RAIL FIELDS ────────── */}
            {shipmentType === "rail" && (
              <>
                {/* MBL list */}
                <div className="cs__multi-section">
                  <div className="cs__multi-header">
                    <span className="cs__multi-label">Master Bill of Lading (MBL) <span className="req">*</span></span>
                    <button type="button" className="cs__add-btn" onClick={() => addArr(railMbls, setRailMbls)}>
                      <MdAdd /> Add MBL
                    </button>
                  </div>
                  {railMbls.map((v, i) => (
                    <div className="cs__multi-row" key={i}>
                      <div className="input">
                        <span className="input-icon"><FaFileInvoice /></span>
                        <input
                          type="text"
                          value={v}
                          onChange={(e) => updateArr(railMbls, setRailMbls, i, e.target.value.replace(/-/g, ""))}
                          placeholder={`MBL# ${i + 1}`}
                        />
                      </div>
                      {railMbls.length > 1 && (
                        <button type="button" className="cs__remove-btn" onClick={() => removeArr(railMbls, setRailMbls, i)}>
                          <MdRemove />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Container list */}
                <div className="cs__multi-section">
                  <div className="cs__multi-header">
                    <span className="cs__multi-label">Container Numbers <span className="req">*</span></span>
                    <button type="button" className="cs__add-btn" onClick={() => addArr(railContainers, setRailContainers)}>
                      <MdAdd /> Add Container
                    </button>
                  </div>
                  {railContainers.map((v, i) => (
                    <div className="cs__multi-row" key={i}>
                      <div className="input">
                        <span className="input-icon"><MdLocalShipping /></span>
                        <input
                          type="text"
                          value={v}
                          onChange={(e) => updateArr(railContainers, setRailContainers, i, e.target.value.replace(/-/g, ""))}
                          placeholder={`Container #${i + 1}`}
                        />
                      </div>
                      {railContainers.length > 1 && (
                        <button type="button" className="cs__remove-btn" onClick={() => removeArr(railContainers, setRailContainers, i)}>
                          <MdRemove />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ────────── DOCUMENTS UPLOAD SECTION ────────── */}
            <div className="cs__documents-upload-section">
              <h4 className="cs__section-title">Upload Documents</h4>
              <div className="cs__documents-grid">
                {/* Arrival Notice / Airway Bill */}
                {shipmentType !== "trucker" && (
                <div className="cs__doc-upload-item">
                  <p className="cs__doc-upload-label">
                    {shipmentType === "air" ? "Arrival Notice / Airway Bill" : "Arrival Notice"} (Optional)
                  </p>
                  <div className="cs__upload-wrapper">
                    <input
                      ref={arrivalNoticeInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.PDF,.JPG,.JPEG,.PNG,.DOC,.DOCX,.XLS,.XLSX"
                      style={{ display: "none" }}
                      onChange={(e) => handleAddFiles(e, setArrivalNoticeFiles)}
                    />
                    <button
                      type="button"
                      className="cs__upload-btn"
                      onClick={() => arrivalNoticeInputRef.current?.click()}
                    >
                      <MdUploadFile />
                      <span className="cs__upload-text">
                        {shipmentType === "air" ? "Upload Arrival Notice / Airway Bill" : "Upload Arrival Notice"}
                      </span>
                    </button>
                  </div>
                  {arrivalNoticeFiles.length > 0 && (
                    <div className="cs__doc-file-list">
                      {arrivalNoticeFiles.map((file, idx) => (
                        <div key={idx} className="cs__doc-file-chip">
                          <span className="cs__file-name">📎 {file.name}</span>
                          <button
                            type="button"
                            className="cs__remove-file-btn"
                            onClick={() => setArrivalNoticeFiles((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}

                {/* Bill of Lading */}
                {shipmentType !== "air" && (
                <div className="cs__doc-upload-item">
                  <p className="cs__doc-upload-label">Bill of Lading (Optional)</p>
                  <div className="cs__upload-wrapper">
                    <input
                      ref={billOfLadingInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.PDF,.JPG,.JPEG,.PNG,.DOC,.DOCX,.XLS,.XLSX"
                      style={{ display: "none" }}
                      onChange={(e) => handleAddFiles(e, setBillOfLadingFiles)}
                    />
                    <button
                      type="button"
                      className="cs__upload-btn"
                      onClick={() => billOfLadingInputRef.current?.click()}
                    >
                      <MdUploadFile />
                      <span className="cs__upload-text">
                        Upload Bill of Lading
                      </span>
                    </button>
                  </div>
                  {billOfLadingFiles.length > 0 && (
                    <div className="cs__doc-file-list">
                      {billOfLadingFiles.map((file, idx) => (
                        <div key={idx} className="cs__doc-file-chip">
                          <span className="cs__file-name">📎 {file.name}</span>
                          <button
                            type="button"
                            className="cs__remove-file-btn"
                            onClick={() => setBillOfLadingFiles((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}

                {/* Commercial Invoice */}
                <div className="cs__doc-upload-item">
                  <p className="cs__doc-upload-label">
                    Commercial Invoice (Optional)
                  </p>
                  <div className="cs__upload-wrapper">
                    <input
                      ref={commercialInvoiceInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.PDF,.JPG,.JPEG,.PNG,.DOC,.DOCX,.XLS,.XLSX"
                      style={{ display: "none" }}
                      onChange={(e) => handleAddFiles(e, setCommercialInvoiceFiles)}
                    />
                    <button
                      type="button"
                      className="cs__upload-btn"
                      onClick={() => commercialInvoiceInputRef.current?.click()}
                    >
                      <MdUploadFile />
                      <span className="cs__upload-text">
                        Upload Commercial Invoice
                      </span>
                    </button>
                  </div>
                  {commercialInvoiceFiles.length > 0 && (
                    <div className="cs__doc-file-list">
                      {commercialInvoiceFiles.map((file, idx) => (
                        <div key={idx} className="cs__doc-file-chip">
                          <span className="cs__file-name">📎 {file.name}</span>
                          <button
                            type="button"
                            className="cs__remove-file-btn"
                            onClick={() => setCommercialInvoiceFiles((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Packing Slip */}
                <div className="cs__doc-upload-item">
                  <p className="cs__doc-upload-label">
                    Packing Slip (Optional)
                  </p>
                  <div className="cs__upload-wrapper">
                    <input
                      ref={packingSlipInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.PDF,.JPG,.JPEG,.PNG,.DOC,.DOCX,.XLS,.XLSX"
                      style={{ display: "none" }}
                      onChange={(e) => handleAddFiles(e, setPackingSlipFiles)}
                    />
                    <button
                      type="button"
                      className="cs__upload-btn"
                      onClick={() => packingSlipInputRef.current?.click()}
                    >
                      <MdUploadFile />
                      <span className="cs__upload-text">
                        Upload Packing Slip
                      </span>
                    </button>
                  </div>
                  {packingSlipFiles.length > 0 && (
                    <div className="cs__doc-file-list">
                      {packingSlipFiles.map((file, idx) => (
                        <div key={idx} className="cs__doc-file-chip">
                          <span className="cs__file-name">📎 {file.name}</span>
                          <button
                            type="button"
                            className="cs__remove-file-btn"
                            onClick={() => setPackingSlipFiles((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="create-shipment-modal__footer">
          <button className="cs__btn cs__btn--danger" onClick={close} disabled={loading}>
            Cancel
          </button>
          <button className="cs__btn cs__btn--primary" onClick={handleCreate} disabled={loading}>
            {loading ? "Creating..." : `Create ${typeLabel} Shipment`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateShipments;
